import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown } from "lucide-react";
import type {
  DomainNode,
  ClusterNode,
  StandardNode,
  StudentMastery,
  MasteryCellInfo,
  Grade,
  GradeMode,
} from "../types/standards";
import { MasteryLevel } from "../types/enums";
import {
  MASTERY_CATEGORIES,
  aggregateCells,
  groupLessons,
  hasDirectMastery,
  masteryLevel,
  overallMastery,
  flattenCodes,
  codeGradeMap,
  instructionLevel,
  gradeLabel,
  bucketLabel,
} from "../service/mastery";
import type { StandardsFrameworkConfig } from "../constants/standardsFrameworkConfig";
import HeatmapTooltip, {
  type TooltipModel,
  type TooltipHandle,
} from "./HeatmapTooltip";
import type { DrawerTarget } from "./StandardDetailDrawer";

function studentGradeLabel(grade: string | undefined): string | null {
  return grade == null ? null : gradeLabel(grade as Grade);
}

function studentSubLabel(
  student: StudentMastery,
  gradeMode: GradeMode,
  codeGrade: Map<string, string>,
): string | null {
  if (gradeMode === "enrolled") {
    const label = studentGradeLabel(student.grade);
    return label ? `Enrolled ${label}` : null;
  }
  const lvl = instructionLevel(student, codeGrade);
  return lvl != null ? `Works at ${bucketLabel(lvl)}` : null;
}

type MasteryDist = {
  mastered: number;
  proficient: number;
  approaching: number;
  not_started: number;
};

function distributionBreakdown(
  heading: string,
  dist: MasteryDist,
  total: number,
): React.ReactNode {
  const segs: Array<[MasteryLevel, number]> = [
    [MasteryLevel.MASTERED, dist.mastered],
    [MasteryLevel.PROFICIENT, dist.proficient],
    [MasteryLevel.APPROACHING, dist.approaching],
    [MasteryLevel.NOT_STARTED, dist.not_started],
  ];
  return (
    <>
      <div className="text-[0.68rem] font-bold uppercase tracking-wide text-[#9AA0A6] mb-1">
        {heading}
      </div>
      <div className="flex h-2 rounded-full overflow-hidden mb-2">
        {segs.map(([lvl, ct]) =>
          ct ? (
            <span
              key={lvl}
              style={{
                width: `${(ct / total) * 100}%`,
                background: MASTERY_CATEGORIES[lvl].dot,
              }}
            />
          ) : null,
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2">
        {segs
          .filter(([, ct]) => ct > 0)
          .map(([lvl, ct]) => (
            <span
              key={lvl}
              className="inline-flex items-center gap-1 text-[0.7rem] text-[#5b6167]"
            >
              <span
                className="inline-block w-2 h-2 rounded-[2px]"
                style={{ background: MASTERY_CATEGORIES[lvl].dot }}
              />
              {MASTERY_CATEGORIES[lvl].label} {ct}
            </span>
          ))}
      </div>
    </>
  );
}

const H_DOMAIN = 58;
const H_CLUSTER = 42;
const H_STD = 34;
const TH_TOTAL = H_DOMAIN + H_CLUSTER + H_STD;

const NAME_W = 218;
const STD_W = 40;
const AGG_W = 40;

const AVATAR_COLORS = [
  "#1C9C6D",
  "#0E7A57",
  "#4F8A6B",
  "#C58B4A",
  "#A0739C",
  "#5E8C7D",
  "#B5654A",
];

type StdLeaf = {
  type: "std";
  id: string;
  label: string;
  std: StandardNode;
  cluster: ClusterNode;
  domain: DomainNode;
};
type ClusterLeaf = {
  type: "cluster";
  id: string;
  label: string;
  cluster: ClusterNode;
  domain: DomainNode;
};
type DomainLeaf = {
  type: "domain";
  id: string;
  label: string;
  domain: DomainNode;
};
type Leaf = StdLeaf | ClusterLeaf | DomainLeaf;

type SortMode = "overall_desc" | "overall_asc" | "name_az";

function avatarOf(name: string) {
  const init = name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return { init, bg: AVATAR_COLORS[h % AVATAR_COLORS.length] };
}

function codesOf(leaf: Leaf): string[] {
  return leaf.type === "std"
    ? [leaf.std.id]
    : leaf.type === "domain"
      ? leaf.domain.clusters.flatMap((c) => c.standards.map((s) => s.id))
      : leaf.cluster.standards.map((s) => s.id);
}

function cellInfo(student: StudentMastery, leaf: Leaf): MasteryCellInfo {
  if (leaf.type === "std") {
    const c = leaf.std.id;
    const cell = student.cells[c];
    return {
      mastery: cell?.mastery ?? 0,
      started: hasDirectMastery(cell),
      evidence: cell?.evidence ?? 0,
      code: c,
    };
  }
  return aggregateCells(student, codesOf(leaf));
}

function cellClass(info: MasteryCellInfo): string {
  return info.started ? `sh-${masteryLevel(info.mastery, true)}` : "sh-ns";
}

interface StandardsHeatmapProps {
  students: StudentMastery[];
  map: DomainNode[];
  framework: StandardsFrameworkConfig;
  gradeMode: GradeMode;
  showAverage: boolean;
  onOpenDetail: (target: DrawerTarget) => void;
}

type StudentRowProps = {
  student: StudentMastery;
  index: number;
  cells: MasteryCellInfo[];
  leaves: Leaf[];
  subLabel: string | null;
  isDimmed: boolean;
};

const StudentRow = memo(function StudentRow({
  student,
  index,
  cells,
  leaves,
  subLabel,
  isDimmed,
}: StudentRowProps) {
  const av = avatarOf(student.name);
  return (
    <tr className={isDimmed ? "sh-row sh-row-dim" : "sh-row"}>
      <th
        data-r={index}
        data-kind="name"
        className="sh-name sticky left-0 z-[12] text-left px-[14px] h-10 border-r border-[#E6EAEE] border-b border-[#EEEEEE] overflow-hidden bg-white"
      >
        <div className="flex items-center gap-[11px] min-w-0">
          <span
            className="flex-none w-8 h-8 rounded-full grid place-items-center text-white font-black text-[0.76rem]"
            style={{ background: av.bg }}
          >
            {av.init}
          </span>
          <div className="flex flex-col min-w-0">
            <span className="font-extrabold text-[#252525] text-[0.86rem] leading-[1.15] truncate">
              {student.name}
            </span>
            {subLabel && (
              <span className="font-semibold text-[#9AA0A6] text-[0.7rem] leading-[1.15] truncate">
                {subLabel}
              </span>
            )}
          </div>
        </div>
      </th>
      {leaves.map((l, ci) => {
        const info = cells[ci];
        return (
          <td
            key={l.id}
            data-c={ci}
            data-r={index}
            data-leaf={l.id}
            data-kind="cell"
            className={`sh-cell p-0 h-10 ${l.type === "std" ? "cursor-pointer" : ""}`}
          >
            {l.type === "std" ? (
              <button
                type="button"
                aria-label={`${student.name}: ${l.std.id}, ${MASTERY_CATEGORIES[info.started ? masteryLevel(info.mastery, true) : MasteryLevel.NOT_STARTED].label}`}
                aria-haspopup="dialog"
                className={`sh-blk grid place-items-center h-10 w-full border-r border-b border-white ${cellClass(info)}`}
              />
            ) : (
              <span
                role="img"
                aria-label={`${student.name}: ${
                  l.type === "cluster"
                    ? `domain ${l.domain.code}, cluster ${l.label}`
                    : `domain ${l.label}`
                }, ${MASTERY_CATEGORIES[info.started ? masteryLevel(info.mastery, true) : MasteryLevel.NOT_STARTED].label}`}
                className={`sh-blk grid place-items-center h-10 w-full border-r border-b border-white ${cellClass(info)}`}
              />
            )}
          </td>
        );
      })}
    </tr>
  );
});

const StandardsHeatmap = ({
  students,
  map,
  framework,
  gradeMode,
  showAverage,
  onOpenDetail,
}: StandardsHeatmapProps) => {
  // Built from the whole framework, not the filtered map: a student's
  // instruction level must not shift as grade pills are toggled.
  const codeGrade = useMemo(() => codeGradeMap(framework.map), [framework]);
  const { terms } = framework;
  const [collapsedD, setCollapsedD] = useState<Set<string>>(new Set());
  const [collapsedC, setCollapsedC] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortMode>("overall_desc");
  const [customSort, setCustomSort] = useState<string | null>(null);
  const [dir, setDir] = useState<1 | -1>(-1);

  const tipRef = useRef<TooltipHandle>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const curColRef = useRef<number | null>(null);
  const curColNodesRef = useRef<NodeListOf<Element> | null>(null);
  const curRowRef = useRef<HTMLElement | null>(null);

  const domRowRef = useRef<HTMLTableRowElement>(null);
  const cluRowRef = useRef<HTMLTableRowElement>(null);
  const stdRowRef = useRef<HTMLTableRowElement>(null);
  const [stickyTop, setStickyTop] = useState({
    h1: H_DOMAIN,
    h2: H_DOMAIN + H_CLUSTER,
    thTotal: TH_TOTAL,
  });

  const setCross = (col: number | null, row: HTMLElement | null) => {
    const box = scrollRef.current;
    if (!box) return;
    if (col !== curColRef.current) {
      if (curColNodesRef.current)
        curColNodesRef.current.forEach((e) => e.classList.remove("col-hl"));
      curColNodesRef.current =
        col !== null ? box.querySelectorAll(`[data-c="${col}"]`) : null;
      if (curColNodesRef.current)
        curColNodesRef.current.forEach((e) => e.classList.add("col-hl"));
      curColRef.current = col;
    }
    if (row !== curRowRef.current) {
      if (curRowRef.current) curRowRef.current.classList.remove("row-hl");
      if (row) row.classList.add("row-hl");
      curRowRef.current = row;
    }
  };
  const clearCross = () => setCross(null, null);

  const showTip = (
    model: TooltipModel,
    e: { clientX: number; clientY: number },
  ) => tipRef.current?.show(model, e.clientX, e.clientY);
  const moveTip = (e: React.MouseEvent) =>
    tipRef.current?.move(e.clientX, e.clientY);
  const hideTip = () => tipRef.current?.hide();
  const hideHover = () => {
    hideTip();
    clearCross();
  };

  const allCodes = useMemo(() => flattenCodes(map), [map]);

  const leafById = useMemo(() => {
    const m = new Map<string, Leaf>();
    map.forEach((d) => {
      m.set("domain:" + d.id, {
        type: "domain",
        id: "domain:" + d.id,
        label: d.code,
        domain: d,
      });
      d.clusters.forEach((c) => {
        m.set("cluster:" + c.id, {
          type: "cluster",
          id: "cluster:" + c.id,
          label: c.letter,
          cluster: c,
          domain: d,
        });
        c.standards.forEach((s) =>
          m.set(s.id, {
            type: "std",
            id: s.id,
            label: s.s,
            std: s,
            cluster: c,
            domain: d,
          }),
        );
      });
    });
    return m;
  }, [map]);

  const leaves = useMemo<Leaf[]>(() => {
    const out: Leaf[] = [];
    map.forEach((d) => {
      if (collapsedD.has(d.id)) {
        out.push({
          type: "domain",
          id: "domain:" + d.id,
          label: d.code,
          domain: d,
        });
        return;
      }
      d.clusters.forEach((c) => {
        if (collapsedC.has(c.id)) {
          out.push({
            type: "cluster",
            id: "cluster:" + c.id,
            label: c.letter,
            cluster: c,
            domain: d,
          });
        } else {
          c.standards.forEach((s) =>
            out.push({
              type: "std",
              id: s.id,
              label: s.s,
              std: s,
              cluster: c,
              domain: d,
            }),
          );
        }
      });
    });
    return out;
  }, [map, collapsedD, collapsedC]);

  useLayoutEffect(() => {
    const measure = () => {
      const h1 = domRowRef.current?.getBoundingClientRect().height ?? H_DOMAIN;
      const h2 = cluRowRef.current?.getBoundingClientRect().height ?? H_CLUSTER;
      const h3 = stdRowRef.current?.getBoundingClientRect().height ?? H_STD;
      setStickyTop((prev) => {
        const next = { h1, h2: h1 + h2, thTotal: h1 + h2 + h3 };
        return prev.h1 === next.h1 &&
          prev.h2 === next.h2 &&
          prev.thTotal === next.thTotal
          ? prev
          : next;
      });
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    [domRowRef, cluRowRef, stdRowRef].forEach((ref) => {
      if (ref.current) observer?.observe(ref.current);
    });
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [leaves]);

  const leafIndex = useMemo(() => {
    const m = new Map<string, number>();
    leaves.forEach((l, i) => m.set(l.id, i));
    return m;
  }, [leaves]);

  const leafCodes = useMemo(() => {
    const m = new Map<string, string[]>();
    leaves.forEach((l) => m.set(l.id, codesOf(l)));
    return m;
  }, [leaves]);

  const cellMatrix = useMemo<MasteryCellInfo[][]>(() => {
    return students.map((student) =>
      leaves.map((leaf) => {
        const codes = leafCodes.get(leaf.id)!;
        return leaf.type === "std"
          ? {
              mastery: student.cells[leaf.std.id]?.mastery ?? 0,
              started: hasDirectMastery(student.cells[leaf.std.id]),
              evidence: student.cells[leaf.std.id]?.evidence ?? 0,
              code: leaf.std.id,
            }
          : aggregateCells(student, codes);
      }),
    );
  }, [students, leaves, leafCodes]);

  const domStart = useMemo(() => {
    const set = new Set<number>();
    let prev: string | null = null;
    leaves.forEach((l, ci) => {
      if (l.domain.id !== prev) {
        if (ci > 0) set.add(ci);
        prev = l.domain.id;
      }
    });
    return set;
  }, [leaves]);

  const sortedStudents = useMemo(() => {
    const arr = students.map((s, i) => ({ s, i }));
    if (customSort) {
      const idx = leafIndex.get(customSort);
      if (idx != null) {
        const keyed = arr.map((o) => {
          const info = cellMatrix[o.i][idx];
          return { o, k: info.started ? info.mastery : -1 };
        });
        keyed.sort(
          (a, b) => dir * (a.k - b.k) || a.o.s.name.localeCompare(b.o.s.name),
        );
        return keyed.map((x) => x.o);
      }
    }
    if (sort === "name_az") {
      arr.sort((a, b) => a.s.name.localeCompare(b.s.name));
      return arr;
    }
    const d = sort === "overall_asc" ? 1 : -1;
    const keyed = arr.map((o) => ({ o, k: overallMastery(o.s, allCodes) }));
    keyed.sort(
      (a, b) => d * (a.k - b.k) || a.o.s.name.localeCompare(b.o.s.name),
    );
    return keyed.map((x) => x.o);
  }, [
    students,
    leaves,
    customSort,
    dir,
    sort,
    allCodes,
    cellMatrix,
    leafIndex,
  ]);

  // Rows with no directly evidenced mastery in the displayed columns fold.
  // The normalized `started` flags also drive cell colors.
  // Display only: stats and counts below still cover every student.
  const [isEmptyRowsExpanded, setIsEmptyRowsExpanded] = useState(false);
  const { activeRows, emptyRows } = useMemo(() => {
    const active: typeof sortedStudents = [];
    const empty: typeof sortedStudents = [];
    sortedStudents.forEach((row) =>
      (cellMatrix[row.i].some((c) => c.started) ? active : empty).push(row),
    );
    return { activeRows: active, emptyRows: empty };
  }, [sortedStudents, cellMatrix]);
  const shouldShowEmptyRows = isEmptyRowsExpanded || activeRows.length === 0;
  const shouldShowEmptyToggle = emptyRows.length > 0 && activeRows.length > 0;

  const leafStats = useMemo(() => {
    return leaves.map((_l, li) => {
      let sum = 0;
      let n = 0;
      const dist = {
        mastered: 0,
        proficient: 0,
        approaching: 0,
        not_started: 0,
      };
      sortedStudents.forEach(({ i }) => {
        const info = cellMatrix[i][li];
        if (info.started) {
          sum += info.mastery;
          n++;
          dist[masteryLevel(info.mastery, true)]++;
        } else {
          dist.not_started++;
        }
      });
      const avg: MasteryCellInfo = {
        mastery: n ? sum / n : 0,
        started: n > 0,
        evidence: n,
        code: null,
        isAggregate: true,
      };
      return {
        avg,
        mean: n ? sum / n : 0,
        n,
        total: sortedStudents.length,
        dist,
      };
    });
  }, [sortedStudents, leaves, cellMatrix]);

  useEffect(() => {
    if (customSort && !leafIndex.has(customSort)) setCustomSort(null);
  }, [customSort, leafIndex]);

  const toggleDomain = (id: string) => {
    setCollapsedD((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleCluster = (id: string) => {
    setCollapsedC((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const onColSort = (id: string) => {
    if (customSort === id) {
      setDir((d) => (d === 1 ? -1 : 1));
    } else {
      setCustomSort(id);
      setDir(-1);
    }
  };

  const cycleStudentSort = () => {
    setCustomSort(null);
    setSort((s) =>
      s === "overall_desc"
        ? "overall_asc"
        : s === "overall_asc"
          ? "name_az"
          : "overall_desc",
    );
  };

  const sortMark = (id: string) =>
    customSort === id ? (dir === 1 ? " ▲" : " ▼") : "";

  const catFor = (info: MasteryCellInfo) =>
    MASTERY_CATEGORIES[
      info.started ? masteryLevel(info.mastery, true) : MasteryLevel.NOT_STARTED
    ];

  const headerDistribution = (leaf: Leaf) => {
    const idx = leafIndex.get(leaf.id);
    if (idx != null) {
      const st = leafStats[idx];
      return { mean: st.mean, n: st.n, total: st.total, dist: st.dist };
    }
    const dist = { mastered: 0, proficient: 0, approaching: 0, not_started: 0 };
    let sum = 0;
    let n = 0;
    sortedStudents.forEach(({ s }) => {
      const ci = cellInfo(s, leaf);
      if (ci.started) {
        sum += ci.mastery;
        n++;
        dist[masteryLevel(ci.mastery, true)]++;
      } else {
        dist.not_started++;
      }
    });
    return { mean: n ? sum / n : 0, n, total: sortedStudents.length, dist };
  };

  const buildCellTip = (leaf: Leaf, rowKey: number | "avg"): TooltipModel => {
    const idx = leafIndex.get(leaf.id);
    const student = rowKey === "avg" ? null : students[rowKey as number];
    const realInfo: MasteryCellInfo = student
      ? idx != null
        ? cellMatrix[rowKey as number][idx]
        : cellInfo(student, leaf)
      : idx != null
        ? leafStats[idx].avg
        : {
            mastery: 0,
            started: false,
            evidence: 0,
            code: null,
            isAggregate: true,
          };
    const cat = catFor(realInfo);
    let who: string;
    let what: string;
    if (leaf.type === "std") {
      who = leaf.std.id;
      what = leaf.std.desc;
    } else if (leaf.type === "cluster") {
      who = framework.isClusterLetterFullCode
        ? leaf.cluster.letter
        : `${leaf.domain.code} ${leaf.cluster.letter}`;
      what = `${leaf.cluster.short} (${terms.cluster} summary)`;
    } else {
      who = `(${leaf.domain.code})`;
      what = `${leaf.domain.name} (${terms.domain} summary)`;
    }
    const name = rowKey === "avg" ? "Class average" : student!.name;

    let body: React.ReactNode;
    let hint: string | undefined;
    if (!realInfo.started) {
      body = (
        <div className="text-[#5b6167]">
          {leaf.type === "std"
            ? `No directly evidenced mastery on this ${terms.standard} yet.`
            : `No evidence yet in this ${terms[leaf.type]}.`}
        </div>
      );
    } else if (leaf.type === "std" && rowKey !== "avg") {
      const lessonGroups = groupLessons(
        student!.cells[leaf.std.id]?.lessons ?? [],
      );
      body = (
        <div className="text-[#5b6167] mt-2 pt-2 border-t border-[#EEEEEE]">
          <div className="text-[0.68rem] font-bold uppercase tracking-wide text-[#9AA0A6] mb-1">
            Lessons &amp; exit tickets
          </div>
          <div className="flex flex-col gap-1">
            {lessonGroups.map((lg) => {
              const lvl =
                lg.total > 0
                  ? masteryLevel(lg.got / lg.total, true)
                  : MasteryLevel.NOT_STARTED;
              return (
                <div
                  key={lg.key}
                  className="flex items-center gap-2 text-[0.72rem]"
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full flex-none"
                    style={{ background: MASTERY_CATEGORIES[lvl].dot }}
                  />
                  <span className="flex-1 truncate">{lg.lesson_title}</span>
                  <span className="text-[#9AA0A6] whitespace-nowrap">
                    {lg.got}/{lg.total} exit ticket problems right
                    {lg.attempts.length > 1
                      ? ` (${lg.attempts.length} attempts)`
                      : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
      hint = "Click for lessons & exit-ticket results";
    } else if (leaf.type === "std") {
      body = (
        <div className="text-[#5b6167]">
          Consolidated across {realInfo.evidence} student
          {realInfo.evidence === 1 ? "" : "s"} with evidence
        </div>
      );
    } else if (rowKey === "avg") {
      body = (
        <div className="text-[#5b6167]">
          Consolidated across {realInfo.evidence} student
          {realInfo.evidence === 1 ? "" : "s"} with evidence
        </div>
      );
    } else {
      const codes = codesOf(leaf);
      const dist = {
        mastered: 0,
        proficient: 0,
        approaching: 0,
        not_started: 0,
      };
      let n = 0;
      codes.forEach((code) => {
        const cell = student!.cells[code];
        if (hasDirectMastery(cell)) {
          n++;
          dist[masteryLevel(cell.mastery, true)]++;
        } else {
          dist.not_started++;
        }
      });
      body = (
        <div>
          {distributionBreakdown("Skill breakdown", dist, codes.length)}
          <div className="text-[#5b6167]">
            Across {n} of {codes.length} {terms.standard}s with evidence
          </div>
        </div>
      );
    }
    return {
      who: name,
      what: (
        <>
          <span className="font-extrabold text-[#02734A]">{who}</span> {what}
        </>
      ),
      statusLabel: cat.label,
      statusColor: realInfo.started ? cat.dot : "#8a9099",
      body,
      hint,
    };
  };

  const buildHeaderTip = (leaf: Leaf): TooltipModel => {
    const info = headerDistribution(leaf);
    const cat =
      MASTERY_CATEGORIES[
        info.n ? masteryLevel(info.mean, true) : MasteryLevel.NOT_STARTED
      ];
    let who: string;
    let what: string;
    if (leaf.type === "std") {
      who = leaf.std.id;
      what = leaf.std.desc;
    } else if (leaf.type === "cluster") {
      who = leaf.cluster.short;
      const code = leaf.cluster.standards[0]?.id
        .split(".")
        .slice(0, 3)
        .join(".");
      what = `${code}, ${leaf.cluster.standards.length} ${terms.standard}${leaf.cluster.standards.length > 1 ? "s" : ""}`;
    } else {
      const skills = leaf.domain.clusters.reduce(
        (a, c) => a + c.standards.length,
        0,
      );
      who = leaf.domain.name;
      const grades = [
        ...new Set(
          leaf.domain.clusters
            .map((c) => c.grade)
            .filter((g): g is Grade => g != null),
        ),
      ];
      const gradeText =
        grades.length > 0
          ? grades.map(gradeLabel).join(", ")
          : leaf.domain.grade != null
            ? gradeLabel(leaf.domain.grade)
            : "";
      what = `${leaf.domain.code}${gradeText ? `, ${gradeText}` : ""}, ${leaf.domain.clusters.length} ${terms.cluster}s, ${skills} ${terms.standard}s`;
    }
    const body = !info.n ? (
      <div className="text-[#5b6167]">
        No directly evidenced mastery for this{" "}
        {leaf.type === "std" ? terms.standard : terms[leaf.type]} yet.
      </div>
    ) : (
      <div>
        {distributionBreakdown("Student distribution", info.dist, info.total)}
        <div className="text-[#5b6167]">
          Consolidated class mastery across {info.n} of {info.total} students
          with evidence
        </div>
      </div>
    );
    return {
      who,
      what,
      statusLabel: cat.label,
      statusColor: info.n ? cat.dot : "#8a9099",
      body,
    };
  };

  const buildStudentTip = (student: StudentMastery): TooltipModel => {
    const dist = { mastered: 0, proficient: 0, approaching: 0, not_started: 0 };
    let sum = 0;
    let n = 0;
    allCodes.forEach((code) => {
      const cell = student.cells[code];
      if (hasDirectMastery(cell)) {
        sum += cell.mastery;
        n++;
        dist[masteryLevel(cell.mastery, true)]++;
      } else {
        dist.not_started++;
      }
    });
    const mean = n ? sum / n : 0;
    const cat =
      MASTERY_CATEGORIES[
        n ? masteryLevel(mean, true) : MasteryLevel.NOT_STARTED
      ];
    const gradeText = studentGradeLabel(student.grade);
    const body = !n ? (
      <div className="text-[#5b6167]">No evidence yet.</div>
    ) : (
      <div>
        {distributionBreakdown("Skill breakdown", dist, allCodes.length)}
        <div className="text-[#5b6167]">
          Across {n} of {allCodes.length} {terms.standard}s with evidence
        </div>
      </div>
    );
    return {
      who: student.name,
      what: `${gradeText ? `${gradeText} ` : ""}overall skill mastery`,
      statusLabel: cat.label,
      statusColor: n ? cat.dot : "#8a9099",
      body,
    };
  };

  const onTableOver = (e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-kind]");
    if (!el) return;
    const kind = el.dataset.kind;
    if (kind === "students") return;
    if (kind === "toggle") {
      hideHover();
      return;
    }
    if (kind === "name") {
      setCross(null, el.closest("tr") as HTMLElement | null);
      const r = el.dataset.r;
      if (r !== undefined) {
        const student = students[Number(r)];
        if (student) showTip(buildStudentTip(student), e);
      }
      return;
    }
    const leaf = leafById.get(el.dataset.leaf ?? "");
    if (!leaf) return;
    const col = el.dataset.c != null ? Number(el.dataset.c) : null;
    if (kind === "header") {
      setCross(col, null);
      showTip(buildHeaderTip(leaf), e);
      return;
    }
    const r = el.dataset.r!;
    const row = r === "avg" ? null : (el.closest("tr") as HTMLElement | null);
    setCross(col, row);
    showTip(buildCellTip(leaf, r === "avg" ? "avg" : Number(r)), e);
  };

  const onTableClick = (e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-kind]");
    if (!el) return;
    const kind = el.dataset.kind;
    if (kind === "students") {
      cycleStudentSort();
      return;
    }
    if (kind === "header") {
      if (el.dataset.sort) onColSort(el.dataset.sort);
      return;
    }
    if (kind === "cell") {
      const r = el.dataset.r;
      if (!r || r === "avg") return;
      const leaf = leafById.get(el.dataset.leaf ?? "");
      if (leaf?.type === "std") {
        hideTip();
        const s = students[Number(r)];
        onOpenDetail({
          student: s,
          std: leaf.std,
          domainCode: leaf.domain.code,
          clusterLetter: leaf.cluster.letter,
          clusterShort: leaf.cluster.short,
        });
      }
    }
  };

  const naturalCols = leaves.reduce(
    (a, l) => a + (l.type === "std" ? STD_W : AGG_W),
    0,
  );
  const naturalWidth = NAME_W + naturalCols;
  const colWidth = (l: Leaf) => (l.type === "std" ? STD_W : AGG_W);
  const gridWidth = naturalWidth;

  return (
    <div className="relative w-full">
      <style>{`
        .sh-cell{transition:background .13s ease}
        .sh-blk{transition:filter .1s, box-shadow .14s ease}
        .sh-blk:focus-visible,th[tabindex]:focus-visible{outline:3px solid #02734A;outline-offset:-3px;position:relative;z-index:32}
        .sh-ns{background-color:#FFFFFF;background-image:repeating-linear-gradient(135deg,transparent 0px,transparent 4px,#C9CDD3 4px,#C9CDD3 5px);box-shadow:inset 0 0 0 1px #E6EAEE !important}
        .sh-approaching{background:${MASTERY_CATEGORIES[MasteryLevel.APPROACHING].dot}}
        .sh-proficient{background:${MASTERY_CATEGORIES[MasteryLevel.PROFICIENT].dot}}
        .sh-mastered{background:${MASTERY_CATEGORIES[MasteryLevel.MASTERED].dot}}
        .sh-row{contain:content}
        .sh-row-dim .sh-name > div,.sh-row-dim .sh-blk{opacity:.45}
        .sh-cell:hover .sh-blk{filter:brightness(1.05)}
        td.sh-cell.col-hl{background:#E7F5EE}
        tr.row-hl td.sh-cell{background:#E7F5EE}
        tr.row-hl th.sh-name{background:#E9F7EF}
        td.sh-cell.col-hl .sh-blk,tr.row-hl td.sh-cell .sh-blk{box-shadow:inset 0 0 0 2px #fff, inset 0 0 0 3px #02734A;position:relative;z-index:1}
        tr.row-hl td.sh-cell.col-hl .sh-blk{box-shadow:inset 0 0 0 2px #fff, inset 0 0 0 4px #02734A;z-index:3}
        th.sh-std.col-hl{background:#1C9C6D !important;color:#fff !important}
      `}</style>
      <div
        ref={scrollRef}
        className="et-standards-table-scroll relative rounded-[10px] border-2 border-[#EEEEEE] bg-white overflow-auto overscroll-x-contain max-h-[72vh] shadow-sm font-mulish font-semibold"
        role="region"
        aria-label="Standard mastery table; scroll for all students and standards"
        tabIndex={0}
      >
        <table
          className="relative border-separate border-spacing-0 text-[0.78rem] table-fixed"
          style={{ width: gridWidth }}
          onMouseOver={onTableOver}
          onMouseMove={moveTip}
          onMouseLeave={hideHover}
          onClick={onTableClick}
          onKeyDown={(event) => {
            if (
              (event.key === "Enter" || event.key === " ") &&
              event.target instanceof HTMLElement &&
              event.target.matches("th[tabindex]")
            ) {
              event.preventDefault();
              event.target.click();
            }
          }}
        >
          <colgroup>
            <col style={{ width: NAME_W }} />
            {leaves.map((l, ci) => (
              <col key={ci} style={{ width: colWidth(l) }} />
            ))}
          </colgroup>

          <thead>
            <tr ref={domRowRef} style={{ height: H_DOMAIN }}>
              <th
                rowSpan={3}
                data-kind="students"
                tabIndex={0}
                title="Change student sort order"
                className="sticky left-0 z-[31] bg-white text-left align-bottom px-4 py-3 border-r border-b border-[#E6EAEE] cursor-pointer"
                style={{ top: 0, height: stickyTop.thTotal }}
              >
                <span className="font-bold text-[0.92rem] text-[#252525] inline-flex items-center gap-[7px]">
                  Student ({sortedStudents.length}){" "}
                  <span className="text-[#9AA0A6] text-[0.78rem]">⇅</span>
                </span>
              </th>
              {map.map((d) => {
                if (collapsedD.has(d.id)) {
                  return (
                    <th
                      key={d.id}
                      rowSpan={3}
                      data-c={leafIndex.get("domain:" + d.id)}
                      data-leaf={"domain:" + d.id}
                      data-kind="header"
                      tabIndex={0}
                      aria-expanded={false}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDomain(d.id);
                      }}
                      className="sticky z-20 bg-[#02734A] text-white align-middle text-center overflow-hidden cursor-pointer border-r border-white/15 border-b border-b-[#EEEEEE]"
                      style={{ top: 0, height: stickyTop.thTotal }}
                    >
                      <div className="flex items-center justify-center h-full py-2">
                        <div className="[writing-mode:vertical-rl] rotate-180 max-h-[100px] max-w-[34px] overflow-hidden">
                          <span className="font-bold text-[0.6rem] text-white/90">
                            ({d.code}) {d.name}
                          </span>
                          <span className="text-[0.62rem] text-white/70 ml-1">
                            ▸
                          </span>
                        </div>
                      </div>
                    </th>
                  );
                }
                const span =
                  leaves.filter((l) => l.domain.id === d.id).length || 1;
                return (
                  <th
                    key={d.id}
                    colSpan={span}
                    data-leaf={"domain:" + d.id}
                    data-kind="header"
                    tabIndex={0}
                    aria-expanded={true}
                    onClick={() => toggleDomain(d.id)}
                    className="sticky z-20 bg-[#02734A] text-white align-middle text-center overflow-hidden border-r border-white/15 border-b border-b-[#EEEEEE] px-[6px] py-[9px] cursor-pointer"
                    style={{ top: 0, height: H_DOMAIN }}
                  >
                    <div className="text-[0.76rem] leading-[1.16] font-extrabold line-clamp-2">
                      {d.name} ({d.code}){" "}
                      <span className="text-white/70 font-normal">▾</span>
                    </div>
                  </th>
                );
              })}
            </tr>

            <tr ref={cluRowRef} style={{ height: H_CLUSTER }}>
              {map.map((d) => {
                if (collapsedD.has(d.id)) return null;
                return d.clusters.map((c) => {
                  const clusterCode = c.letter;
                  if (collapsedC.has(c.id)) {
                    return (
                      <th
                        key={c.id}
                        rowSpan={2}
                        data-c={leafIndex.get("cluster:" + c.id)}
                        data-leaf={"cluster:" + c.id}
                        data-kind="header"
                        tabIndex={0}
                        aria-expanded={false}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCluster(c.id);
                        }}
                        className="sticky z-20 bg-[#B7E3CC] text-[#0a4d31] align-middle text-center overflow-hidden cursor-pointer border-r border-[#0a4d31]/10 border-b border-b-[#EEEEEE]"
                        style={{
                          top: stickyTop.h1,
                          height: stickyTop.thTotal - stickyTop.h1,
                        }}
                      >
                        <div className="flex flex-col items-center justify-center gap-2 h-full py-2">
                          <span className="inline-flex items-center justify-center rounded-md px-[7px] py-[2px] text-[0.62rem] font-bold bg-[#0a4d31]/15 text-[#0a4d31]">
                            {clusterCode}
                          </span>
                          <span className="[writing-mode:vertical-rl] rotate-180 font-bold text-[0.6rem] max-h-[48px] max-w-[34px] overflow-hidden">
                            {c.short}
                          </span>
                        </div>
                      </th>
                    );
                  }
                  return (
                    <th
                      key={c.id}
                      colSpan={c.standards.length}
                      data-leaf={"cluster:" + c.id}
                      data-kind="header"
                      tabIndex={0}
                      aria-expanded={true}
                      onClick={() => toggleCluster(c.id)}
                      className="sticky z-20 bg-[#B7E3CC] text-[#0a4d31] font-extrabold align-middle text-center overflow-hidden border-r border-[#0a4d31]/10 border-b border-b-[#EEEEEE] px-1 py-[7px] leading-[1.14] cursor-pointer"
                      style={{ top: stickyTop.h1, height: H_CLUSTER }}
                    >
                      <div className="flex items-center justify-center gap-[5px] text-[0.7rem] mb-[3px]">
                        <span className="inline-flex items-center justify-center rounded-md px-[7px] py-[2px] text-[0.62rem] font-bold bg-[#0a4d31]/15 text-[#0a4d31]">
                          {clusterCode}
                        </span>
                      </div>
                      <div className="text-[0.6rem] leading-[1.12] line-clamp-2">
                        {c.short}
                      </div>
                    </th>
                  );
                });
              })}
            </tr>

            <tr ref={stdRowRef} style={{ height: H_STD }}>
              {leaves.map((l, ci) => {
                if (l.type !== "std") return null;
                return (
                  <th
                    key={l.id}
                    data-c={ci}
                    data-leaf={l.id}
                    data-kind="header"
                    data-sort={l.std.id}
                    tabIndex={0}
                    aria-label={`Sort by ${l.std.id}`}
                    aria-sort={
                      customSort === l.std.id
                        ? dir === 1
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    className={`sh-std sticky z-20 text-[0.66rem] font-extrabold h-[34px] truncate cursor-pointer border-r border-b border-[#EEEEEE] tracking-wide bg-[#F6F8F9] text-[#252525] hover:bg-[#EAF4EE] ${domStart.has(ci) ? "shadow-[inset_2px_0_0_#E6EAEE]" : ""}`}
                    style={{ top: stickyTop.h2 }}
                  >
                    {l.label}
                    {sortMark(l.std.id)}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {showAverage && (
              <tr>
                <th
                  className="sticky left-0 z-30 bg-[#B7E3CC] text-[#0a4d31] text-left px-[14px] align-middle border-r border-[#0a4d31]/10"
                  style={{ top: stickyTop.thTotal, height: 40 }}
                >
                  <span className="font-extrabold text-[0.86rem] leading-[1.15]">
                    Average
                  </span>
                </th>
                {leaves.map((l, ci) => {
                  const a = leafStats[ci].avg;
                  return (
                    <td
                      key={l.id}
                      data-c={ci}
                      data-r="avg"
                      data-leaf={l.id}
                      data-kind="cell"
                      className="sh-cell sticky z-[15] p-0 bg-white"
                      style={{ top: stickyTop.thTotal, height: 40 }}
                    >
                      <span
                        className={`sh-blk grid place-items-center h-10 w-full border-r border-b border-white ${cellClass(a)}`}
                      />
                    </td>
                  );
                })}
              </tr>
            )}

            {activeRows.map(({ s, i }) => (
              <StudentRow
                key={i}
                student={s}
                index={i}
                cells={cellMatrix[i]}
                leaves={leaves}
                subLabel={studentSubLabel(s, gradeMode, codeGrade)}
                isDimmed={false}
              />
            ))}

            {shouldShowEmptyToggle && (
              <tr>
                <td
                  colSpan={leaves.length + 1}
                  data-kind="toggle"
                  className="p-0 border-b border-[#EEEEEE]"
                >
                  <button
                    type="button"
                    onClick={() => setIsEmptyRowsExpanded((v) => !v)}
                    aria-expanded={isEmptyRowsExpanded}
                    className="block w-full h-10 text-left bg-white hover:bg-[#F6F8F9] text-[#5b6167] hover:text-[#252525] transition-colors"
                  >
                    {/* Sticky so the label stays in view while the grid scrolls sideways. */}
                    <span className="sticky left-0 inline-flex items-center gap-[6px] px-[14px] font-bold text-[0.78rem] whitespace-nowrap">
                      <ChevronDown
                        aria-hidden="true"
                        className={`w-4 h-4 transition-transform ${isEmptyRowsExpanded ? "rotate-180" : ""}`}
                      />
                      {emptyRows.length}{" "}
                      {emptyRows.length === 1 ? "student" : "students"} with no
                      data in the selected standards
                    </span>
                  </button>
                </td>
              </tr>
            )}

            {shouldShowEmptyRows &&
              emptyRows.map(({ s, i }) => (
                <StudentRow
                  key={i}
                  student={s}
                  index={i}
                  cells={cellMatrix[i]}
                  leaves={leaves}
                  subLabel={studentSubLabel(s, gradeMode, codeGrade)}
                  isDimmed
                />
              ))}
          </tbody>
        </table>
      </div>
      <HeatmapTooltip ref={tipRef} />
    </div>
  );
};

export default StandardsHeatmap;
